import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { LogOut, Moon, Sun, UserRound } from 'lucide-react';
import { useCallback, useState } from 'react';

import {
  getAuthContexts,
  postAuthSwitchContext,
  type LoginContextOption
} from '../../entities/me/me.api';
import { ApiError } from '../../shared/api/api-error';
import { useAuth } from '../../shared/auth/use-auth';
import { useStaffTheme } from '../../shared/theme/staff-theme-context';
import { Button } from '../../shared/ui/Button';
import { ModalDialog } from '../../shared/ui/ModalDialog';
import { staffRoleLabel } from './staff-role-label';

const MENU_ITEM_CLASS =
  'flex w-full cursor-pointer select-none items-center gap-2 rounded-[var(--ui-radius-control)] px-2 py-2 text-left text-sm text-[hsl(var(--ui-foreground))] outline-none transition-colors data-[highlighted]:bg-[hsl(var(--ui-muted))] focus-visible:ring-2 focus-visible:ring-[hsl(var(--ui-ring)/0.35)]';

/**
 * Блок профиля в топбаре: текущий раздел входа, «Сменить роль», тема и выход.
 * На узких экранах остаётся только аватар, действия — в меню.
 */
export function StaffProfileMenu() {
  const { me, apiClient, applySessionTokens, logout } = useAuth();
  const { theme, toggleTheme } = useStaffTheme();
  const [open, setOpen] = useState(false);
  const [contexts, setContexts] = useState<LoginContextOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const displayName = (me?.full_name ?? '').trim() || me?.email || '—';
  const contextLabel = me?.context?.label?.trim() || null;
  const roleLabel = contextLabel || staffRoleLabel(me?.role);
  const avatarLabel = (displayName.trim()[0] ?? '?').toLocaleUpperCase('ru-RU');
  const isDark = theme === 'dark';

  const openContexts = useCallback(async () => {
    setOpen(true);
    setLoading(true);
    setError(null);
    try {
      const res = await getAuthContexts(apiClient);
      setContexts(res.available ?? []);
    } catch (e) {
      setContexts([]);
      setError(e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Не удалось загрузить роли.');
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  const switchTo = useCallback(
    async (context: LoginContextOption) => {
      setBusyId(context.id);
      setError(null);
      try {
        const tokens = await postAuthSwitchContext(apiClient, context.id);
        if (!tokens.access_token) throw new Error('Не получен access_token');
        await applySessionTokens(tokens.access_token, tokens.refresh_token ?? null);
        setOpen(false);
        window.location.assign('/');
      } catch (e) {
        setError(e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Не удалось сменить роль.');
      } finally {
        setBusyId(null);
      }
    },
    [apiClient, applySessionTokens]
  );

  return (
    <>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            data-testid="staff-profile-trigger"
            className="flex min-w-0 max-w-[12rem] shrink-0 items-center gap-2 rounded-[var(--ui-radius-control)] border-0 bg-transparent p-0 text-right outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ui-ring)/0.35)] lg:max-w-[16rem]"
            aria-label={`Профиль: ${roleLabel}, ${displayName}`}
          >
            <span className="hidden min-w-0 flex-col justify-center md:flex">
              <span className="min-w-0 truncate text-xs leading-tight text-[hsl(var(--ui-muted-foreground))]">
                {contextLabel ? (
                  <>
                    Вы вошли как:{' '}
                    <span className="text-[hsl(var(--ui-foreground))]">{contextLabel}</span>
                  </>
                ) : (
                  roleLabel
                )}
              </span>
              <span className="min-w-0 truncate text-sm font-medium leading-tight text-[hsl(var(--ui-foreground))]">
                {displayName}
              </span>
            </span>
            <span
              className="flex size-9 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--ui-muted))] text-xs font-semibold uppercase text-[hsl(var(--ui-foreground))] md:hidden"
              aria-hidden
            >
              {avatarLabel}
            </span>
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end"
            sideOffset={6}
            collisionPadding={8}
            className="z-[6000] w-64 max-w-[calc(100vw-1.5rem)] rounded-[var(--ui-radius-control)] border border-[hsl(var(--ui-border))] bg-[hsl(var(--ui-card))] p-1.5 text-[hsl(var(--ui-card-foreground))] shadow-[var(--ui-shadow-elevated)] outline-none"
          >
            <DropdownMenu.Label className="px-2 py-1.5">
              <span className="block truncate text-xs text-[hsl(var(--ui-muted-foreground))]">
                {contextLabel ? `Вы вошли как: ${contextLabel}` : roleLabel}
              </span>
              <span className="block truncate text-sm font-medium">{displayName}</span>
            </DropdownMenu.Label>
            {me?.has_other_contexts ? (
              <DropdownMenu.Item
                data-testid="staff-profile-switch-role"
                className={MENU_ITEM_CLASS}
                onSelect={() => void openContexts()}
              >
                <UserRound className="size-4 shrink-0" aria-hidden />
                Сменить роль
              </DropdownMenu.Item>
            ) : null}
            <DropdownMenu.Separator className="my-1 h-px bg-[hsl(var(--ui-border))]" />
            <DropdownMenu.Item
              data-testid="staff-profile-theme"
              className={MENU_ITEM_CLASS}
              onSelect={() => toggleTheme()}
            >
              {isDark ? <Sun className="size-4 shrink-0" aria-hidden /> : <Moon className="size-4 shrink-0" aria-hidden />}
              {isDark ? 'Светлая тема' : 'Тёмная тема'}
            </DropdownMenu.Item>
            <DropdownMenu.Item
              data-testid="staff-profile-logout"
              className={MENU_ITEM_CLASS}
              onSelect={() => void logout()}
            >
              <LogOut className="size-4 shrink-0" aria-hidden />
              Выход
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      <ModalDialog
        open={open}
        onOpenChange={setOpen}
        title="Сменить роль"
        description="Выберите, с какой ролью продолжить работу."
        dataTestId="staff-context-switch-dialog"
        footer={
          <Button type="button" variant="secondary" disabled={busyId !== null} onClick={() => setOpen(false)}>
            Закрыть
          </Button>
        }
      >
        {loading ? (
          <p className="text-sm text-[hsl(var(--ui-muted-foreground))]">Загружаем роли…</p>
        ) : contexts.length === 0 ? (
          <p className="text-sm text-[hsl(var(--ui-muted-foreground))]">Других ролей нет.</p>
        ) : (
          <div className="space-y-2">
            {contexts.map((context) => (
              <button
                key={context.id}
                type="button"
                data-testid={`staff-context-option-${context.id}`}
                disabled={busyId !== null}
                onClick={() => void switchTo(context)}
                className="flex w-full items-center justify-between rounded-[var(--ui-radius-control)] border border-[hsl(var(--ui-border))] bg-[hsl(var(--ui-card))] px-3 py-2.5 text-left text-sm transition-colors hover:bg-[hsl(var(--ui-muted))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ui-ring)/0.35)] disabled:opacity-60"
              >
                <span className="min-w-0 truncate">{context.label}</span>
                <span className="ml-3 shrink-0 text-[hsl(var(--ui-muted-foreground))]">
                  {busyId === context.id ? 'Входим…' : '→'}
                </span>
              </button>
            ))}
          </div>
        )}
        {error ? (
          <p className="mt-2 text-sm text-[hsl(var(--ui-destructive))]" role="alert">
            {error}
          </p>
        ) : null}
      </ModalDialog>
    </>
  );
}
